import {
  endOfMonth,
  endOfQuarter,
  endOfYear,
  format,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subDays,
} from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FiXCircle } from 'react-icons/fi';
import { IoCalendarOutline, IoChevronDownSharp } from 'react-icons/io5';
import styled, { useTheme } from 'styled-components';
import TooltipButton from '../components/Tools/TooltipButton.tsx';

interface DateRangeSelectorProps {
  initialStartDate?: Date | null;
  initialEndDate?: Date | null;
  onChange?: (startDate: Date | null, endDate: Date | null) => void;
  clearTrigger?: any;
  label?: string;
}

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
  flex-direction: column;
  width: auto;            // Only as wide as content
  min-width: 120px;       // Optional: provide a small minimum if desired
  max-width: 100%;        // Prevent overflow
`;

const ToggleButton = styled.button<{ theme: any; hasValue: boolean }>`
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  width: 100%;
  &:hover {
    background-color: ${({ theme }) => theme.colors.hoverBackground};
  }
    overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const Menu = styled.div<{ theme: any }>`
  position: absolute;
  top: 110%;
  left: 0;
  background-color: ${({ theme }) => theme.colors.card};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 0.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  z-index: 1000;
  min-width: 280px;
  color: ${({ theme }) => theme.colors.text};

  /* Ensure the datepicker wrapper takes full width to prevent layout shifts */
  .react-datepicker-wrapper {
    width: 100%;
  }
`;

const PresetList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 1rem;
`;

const PresetItem = styled.li`
  margin-top: 0.5rem;
`;

const PresetButton = styled.button<{ active: boolean; theme: any }>`
  background: ${({ active, theme }) =>
    active ? theme.colors.selectedBackground : 'transparent'};
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  width: 100%;
  text-align: left;
  border-radius: 0.25rem;

  &:hover {
    background-color: ${({ theme }) => theme.colors.hoverBackground};
  }
`;

const Note = styled.p`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textSecondary || '#777'};
`;

const LabelWrapper = styled.div`
   position: relative;
  display: block;
  width: auto;            // Auto width based on label size
  max-width: 100%;
`;

const FloatingLabel = styled.label<{ isFloating: boolean }>`
  position: absolute;
  left: ${({ isFloating }) => (isFloating ? '0.50rem' : '2.4em')};
  top: ${({ isFloating }) => (isFloating ? '-10px' : '50%')};
  transform: translateY(${({ isFloating }) => (isFloating ? '0' : '-50%')});
  font-size: ${({ isFloating }) => (isFloating ? '0.75rem' : '0.95rem')};
  background:${({ isFloating }) => (isFloating ? 'white' : 'transparent')};
  color:${({ isFloating, theme }) => (isFloating ? theme.colors.blankandwhite : theme.colors.text)};
  padding: 0 4px;
  pointer-events: none;
  transition: all 0.2s ease;
  z-index: 2;
  white-space: normal;
  word-break: break-word;
  max-width: 100%;
  overflow: visible;
`;

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  initialStartDate = null,
  initialEndDate = null,
  onChange,
  clearTrigger,
  label = 'Select Dates',
}) => {
  const theme = useTheme();
  const [startDate, setStartDate] = useState<Date | null>(initialStartDate);
  const [endDate, setEndDate] = useState<Date | null>(initialEndDate);
  const [showMenu, setShowMenu] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string>(
    initialStartDate && initialEndDate ? 'Custom Range' : 'Last 7 Days'
  );
  const manualChangeRef = useRef(false);

  const didMountRef = useRef(false);
  const lastClear = useRef<any>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (clearTrigger !== undefined && clearTrigger !== lastClear.current) {
      setStartDate(null);
      setEndDate(null);
      setSelectedLabel('Custom Range');
      setCustomMode(true);
      lastClear.current = clearTrigger;
    }
  }, [clearTrigger]);

 useEffect(() => {
  if (initialStartDate && initialEndDate) {
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);

    // Check for matching preset
    const matchedPreset = PRESETS.find(preset => {
      const [presetStart, presetEnd] = preset.getRange();
      return (
        presetStart &&
        presetEnd &&
        initialStartDate &&
        initialEndDate &&
        format(presetStart, 'yyyy-MM-dd') === format(initialStartDate, 'yyyy-MM-dd') &&
        format(presetEnd, 'yyyy-MM-dd') === format(initialEndDate, 'yyyy-MM-dd')
      );
    });

    setSelectedLabel(matchedPreset?.label ?? 'Custom Range');
    setCustomMode(matchedPreset?.label === 'Custom Range' || !matchedPreset);
  }
}, [initialStartDate, initialEndDate]);


  const toggleMenu = () => setShowMenu((prev) => !prev);

  const PRESETS: {
    label: string;
    getRange: () => [Date | null, Date | null];
  }[] = [
      { label: 'Today', getRange: () => [new Date(), new Date()] },
      { label: 'Last 7 Days', getRange: () => [subDays(new Date(), 6), new Date()] },
      { label: 'This Month', getRange: () => [startOfMonth(new Date()), endOfMonth(new Date())] },
      { label: 'This Quarter', getRange: () => [startOfQuarter(new Date()), endOfQuarter(new Date())] },
      { label: 'This Year', getRange: () => [startOfYear(new Date()), endOfYear(new Date())] },
      { label: 'Custom Range', getRange: () => [startDate, endDate] },
    ];

  const handlePresetClick = (label: string, getRange: () => [Date | null, Date | null]) => {
    const [start, end] = getRange();
    setStartDate(start);
    setEndDate(end);
    setSelectedLabel(label);
    setCustomMode(label === 'Custom Range');
    
    // Automatically apply and close menu if it's a preset
    if (label !== 'Custom Range') {
      manualChangeRef.current = true;
      onChange?.(start, end);
      setShowMenu(false);
    }
  };

  const handleApplyCustomRange = () => {
    manualChangeRef.current = true;
    onChange?.(startDate, endDate);
    setShowMenu(false);
  };

  const handleClearCustomRange = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const formatDateRange = () => {
    if (selectedLabel && selectedLabel !== 'Custom Range') return selectedLabel;
    if (!startDate || !endDate) return '';
    const sameDay = format(startDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd');
    return sameDay
      ? format(startDate, 'MMM d, yy')
      : `${format(startDate, 'MMM d, yy')} – ${format(endDate, 'MMM d, yy')}`;
  };

  const showFloating = !!startDate && !!endDate;
  const isFlowting = showFloating || !!formatDateRange();
  
  return (
    <Wrapper ref={wrapperRef}>
      <LabelWrapper>
        <FloatingLabel isFloating={isFlowting}>
          <span className='text-nowrap'>
            {label}
          </span>
        </FloatingLabel>
        <TooltipButton title={!showFloating ? label : label + ": " + formatDateRange()}>
          <ToggleButton onClick={toggleMenu} theme={theme} hasValue={!!formatDateRange()} style={{ minWidth: "126px", justifyContent: 'space-between' }}>
            <div style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', flex: 1 }}>
              <IoCalendarOutline size={16} style={{ marginRight: '0.5rem' }} />
              <span className={!isFlowting ? 'opacity-0' : 'text-default fw-bold'}>
                {formatDateRange() || label}
              </span>
            </div>
            <IoChevronDownSharp size={20} style={{ marginLeft: '0.5rem' }} />
          </ToggleButton>
        </TooltipButton>
      </LabelWrapper>

      {showMenu && (
        <Menu theme={theme}>
          <div className='d-flex flex-row align-items-center justify-content-between w-100'>
            <strong>Quick Select</strong>
            {
              startDate != null &&
              <button
                type="button"
                className="btn btn-sm btn-link text-danger p-0"
                title="Clear selection"
                onClick={() => {
                  handlePresetClick("", () => [null, null]);
                }}
              >
                <FiXCircle size={18} />
              </button>
            }
          </div>
          <PresetList>
            {PRESETS.map(({ label, getRange }) => (
              <PresetItem key={label}>
                <PresetButton
                  onClick={() => handlePresetClick(label, getRange)}
                  active={selectedLabel == label}
                  theme={theme}
                >
                  {label}
                </PresetButton>
              </PresetItem>
            ))}
          </PresetList>

          {customMode && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.5rem' }}>
              {/* Changed from <label> to <div> to stop click bubbling */}
              <div className='d-flex flex-row align-items-center justify-content-between gap-2'>
                <span style={{ minWidth: '45px' }}>From:</span>
                <div style={{ flex: 1 }}>
                  <DatePicker
                    selected={startDate ?? undefined}
                    onChange={(date) => {
                      manualChangeRef.current = true;
                      setStartDate(date)
                    }}
                    selectsStart
                    startDate={startDate ?? undefined}
                    endDate={endDate ?? undefined}
                    dateFormat="MMM d, yyyy"
                    className="form-control w-100"
                    wrapperClassName="w-100"
                    shouldCloseOnSelect={true}
                    isClearable
                  />
                </div>
              </div>
              
              {/* Changed from <label> to <div> to stop click bubbling */}
              <div className='d-flex flex-row align-items-center justify-content-between gap-2'>
                <span style={{ minWidth: '45px' }}>To:</span>
                <div style={{ flex: 1 }}>
                  <DatePicker
                    selected={endDate ?? undefined}
                    onChange={(date) => {
                      manualChangeRef.current = true;
                      setEndDate(date);
                    }}
                    selectsEnd
                    startDate={startDate ?? undefined}
                    endDate={endDate ?? undefined}
                    minDate={startDate ?? undefined}
                    dateFormat="MMM d, yyyy"
                    className="form-control w-100"
                    wrapperClassName="w-100"
                    shouldCloseOnSelect={true}
                    isClearable
                  />
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2 mt-2">
                <button 
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleClearCustomRange}
                >
                  Clear
                </button>
                <button 
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleApplyCustomRange}
                >
                  Apply
                </button>
              </div>
            </div>
          )}

          <Note theme={theme}>📌 Tip: Use preset ranges or choose your own custom dates.</Note>
        </Menu>
      )}
    </Wrapper>
  );
};

export default DateRangeSelector;